import { DM_LIVES } from "@neon-arena/shared";

/**
 * Lives → scoreboard text.
 *
 * The queue's deathmatch profile hands every player {@link DM_LIVES} (9999) —
 * the "unlimited lives" sentinel (`shared/src/constants/gameConstants.ts`), used
 * because `Infinity` does not survive the schema encoder (lobby-upgrade-plan
 * §8b gotcha 1). The scoreboard renders one ♥ per life, so without a special
 * case a DM round would draw ~9999 hearts per row.
 *
 * NOTE: checking for the exact value 9999 is NOT sufficient. `recordDeath()`
 * (`shared/src/simulation/combat.ts`) does `lives = Math.max(0, lives - 1)` in
 * every mode, so a DM player's lives read 9998, 9997, … after each death. The
 * test is therefore "implausibly many lives" rather than "=== DM_LIVES": any
 * count above {@link MAX_LIFE_PIPS} is unlimited. That also catches the
 * `Number.MAX_SAFE_INTEGER` an `Infinity` would decode to.
 */

/** Most lives any real mode grants (LMS gives 3). Above this = unlimited. */
export const MAX_LIFE_PIPS = 10;

export function isUnlimitedLives(lives: number): boolean {
  return Number.isFinite(lives) ? lives > MAX_LIFE_PIPS : true;
}

/** ♥ pips, or "∞" for the deathmatch profile. Never returns a huge string. */
export function formatLives(lives: number | undefined): string {
  const n = lives ?? 3;
  if (isUnlimitedLives(n)) return "∞";
  return "♥".repeat(Math.max(0, Math.floor(n)));
}

// Referenced so the sentinel's definition site is greppable from here.
export { DM_LIVES };
